import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FifBranch, FifBranchService } from '@lmc-app/core/services/fif-branch.service';

export interface BranchDialogData {
  branch?: FifBranch;
}

interface BranchModel {
  dprn: string;
  name: string;
  address: string;
}

@Component({
  selector: 'app-branch-dialog',
  templateUrl: './branch-dialog.component.html',
  styleUrls: ['./branch-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyMaterialModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
})
export class BranchDialogComponent {
  readonly isEdit: boolean;
  isSaving = false;

  form = new FormGroup({});
  model: BranchModel = { dprn: '', name: '', address: '' };
  fields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-12',
          key: 'dprn',
          type: 'input',
          props: {
            label: 'Routing Number (DPRN)',
            description: '9 digits: 0 + institution (3) + transit (5)',
            required: true,
            maxLength: 9,
            pattern: /^\d{9}$/,
            appearance: 'outline',
            attributes: { autocomplete: 'off' },
          },
          validation: {
            messages: {
              pattern: 'Routing number must be exactly 9 digits',
            },
          },
        },
        {
          className: 'col-12',
          key: 'name',
          type: 'input',
          props: {
            label: 'Institution Name',
            required: true,
            maxLength: 255,
            appearance: 'outline',
            attributes: { autocomplete: 'off' },
          },
        },
        {
          className: 'col-12',
          key: 'address',
          type: 'input',
          props: {
            label: 'Address',
            required: true,
            maxLength: 512,
            appearance: 'outline',
            attributes: { autocomplete: 'off' },
          },
        },
      ],
    },
  ];

  constructor(
    private readonly dialogRef: MatDialogRef<BranchDialogComponent, FifBranch>,
    private readonly fifBranchService: FifBranchService,
    private readonly snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public readonly data: BranchDialogData
  ) {
    this.isEdit = !!data?.branch;
    if (data?.branch) {
      this.model = {
        dprn: data.branch.dprn,
        name: data.branch.name,
        address: data.branch.address,
      };
    }
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = {
      dprn: this.model.dprn.trim(),
      name: this.model.name.trim(),
      address: this.model.address.trim(),
    };

    this.isSaving = true;

    const request$ =
      this.isEdit && this.data.branch
        ? this.fifBranchService.update(this.data.branch.id, payload)
        : this.fifBranchService.create(payload);

    request$.subscribe({
      next: (res) => {
        this.snackBar.open(`Branch ${this.isEdit ? 'updated' : 'created'} successfully`, 'Close', {
          duration: 4000,
        });
        this.dialogRef.close(res.data);
      },
      error: (err) => {
        const msg = err.error?.message || `Failed to ${this.isEdit ? 'update' : 'create'} branch`;
        this.snackBar.open(msg, 'Close', { duration: 5000 });
        this.isSaving = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}